import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { getTenantSettings } from '../settings/tenant-api';
import { readableFailure } from './failures';
import { StoredAvatar } from '../ui/StoredAvatar';
import { formatDateTime, formatRelative } from '../ui/format';
import { Badge, EmptyState, ErrorState, Loading, type BadgeTone } from '../ui/Page';
import { SearchGlyph } from '../crm/BoardIcons';
import { useRealtime, useRealtimeReconnect, useRealtimeTopic } from '../app/realtime-store';
import { ChannelMark } from './ChannelIcons';
import { AttachButton, AttachmentTray, RecordButton, type PendingAttachment } from './Attachments';
import { ContactDrawer } from './ContactDrawer';
import { PolishButtons, ScheduleButton } from './ComposerTools';
import { ThreadTools } from './ThreadTools';
import { TransferDialog } from './TransferDialog';
import { MessageMedia } from './MessageMedia';
import { NewConversationDialog } from './NewConversationDialog';
import { AvailabilityPicker } from './Availability';
import {
  cancelScheduled,
  type ChannelKind,
  type ConversationDetail,
  type ConversationStatus,
  type ConversationSummary,
  deleteForCustomer,
  getConversation,
  getSessionWindow,
  INTERACTIVE_LIMITS,
  listChannels,
  listConversations,
  listQuickReplies,
  listScheduled,
  type MessageView,
  noteQuickReplyUse,
  type QuickReply,
  retryMessage,
  scheduleMessage,
  sendInteractive,
  sendReply,
  sendTemplate,
  type SessionWindow,
  setConversationHandling,
  setConversationStatus,
} from './api';

/**
 * The inbox (context.md sections 45-56).
 *
 * Three columns, because that is the shape of the work: which conversations are waiting, the thread,
 * and what is known about who is on the other end. On a narrow screen it becomes two screens — the
 * list, then the thread — since a conversation on a phone is one thing at a time.
 *
 * The one thing this screen does that no other list does is refuse to let an agent type into a
 * WhatsApp conversation whose 24-hour window has closed. That is not politeness: outside the window
 * the message is rejected by Meta and the rejection costs the tenant's number its quality rating.
 */

type ViewKey = 'mine' | 'unassigned' | 'all';

/**
 * The three questions an agent opens this screen with, in the order they ask them.
 *
 * "Todas" first because the shape of the queue is what somebody checks before deciding what to
 * pick up; "Minhas" is what they are already holding; "Fila" is what nobody has taken.
 */
const VIEWS: { key: ViewKey; labelKey: string }[] = [
  { key: 'all', labelKey: 'inbox.view.all' },
  { key: 'mine', labelKey: 'inbox.view.mine' },
  { key: 'unassigned', labelKey: 'inbox.view.unassigned' },
];

const STATUSES: { key: ConversationStatus | 'ALL'; labelKey: string }[] = [
  { key: 'OPEN', labelKey: 'inbox.status.OPEN' },
  { key: 'PENDING', labelKey: 'inbox.status.PENDING' },
  { key: 'RESOLVED', labelKey: 'inbox.status.RESOLVED' },
  { key: 'ALL', labelKey: 'inbox.status.ALL' },
];

export function InboxPage(): React.JSX.Element {
  const [view, setView] = useState<ViewKey>('all');
  const [status, setStatus] = useState<ConversationStatus | 'ALL'>('OPEN');
  const [channelId, setChannelId] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels, staleTime: 60_000 });

  const conversations = useQuery({
    queryKey: ['inbox', view, status, search],
    queryFn: () =>
      listConversations({
        view,
        ...(status === 'ALL' ? {} : { status }),
        ...(search.trim() ? { search: search.trim() } : {}),
        limit: 50,
      }),
    /*
     * The socket is what makes this current; the interval is the floor under it.
     *
     * Not fifteen seconds any more — a message now arrives in the frame it was published in. But a
     * socket that is silently broken looks exactly like a quiet afternoon, so a slow refetch stays
     * as the thing that notices (context.md §40).
     */
    refetchInterval: 60_000,
  });

  const client = useQueryClient();
  const { connected } = useRealtime();

  const refreshList = useCallback(() => {
    void client.invalidateQueries({ queryKey: ['inbox'] });
  }, [client]);

  // Everything that happened while the socket was down was missed, so coming back means refetching.
  useRealtimeTopic('inbox', refreshList);
  useRealtimeReconnect(refreshList);

  const all = conversations.data?.conversations ?? [];

  // Filtered here rather than on the server: the channel list is short, the page is already loaded,
  // and a round trip per click on a filter that narrows fifty rows is a round trip for nothing.
  const rows = channelId ? all.filter((row) => row.channelId === channelId) : all;

  /*
   * The chime moved to the top bar (`MessagesBell`).
   *
   * It used to live here, which meant it only ever rang for somebody already looking at the inbox —
   * the one moment they do not need telling. It is now mounted on every route, and firing it here
   * as well would simply sound it twice.
   */

  const kindOf = (id: string): ChannelKind =>
    (channels.data?.channels.find((row) => row.id === id)?.kind ?? 'WEBCHAT') as ChannelKind;

  /*
   * Two groups, because they are two different jobs.
   *
   * Somebody is answering the first set; nobody is answering the second, and the second is the one
   * with a clock on it. Sorting them into one list by time buries the waiting ones among threads
   * that are already being handled.
   */
  const handled = rows.filter((row) => row.assigneeId !== null && row.status === 'OPEN');
  const waiting = rows.filter((row) => row.assigneeId === null || row.status !== 'OPEN');

  /*
   * Resolved today, counted from the rows already loaded.
   *
   * Deliberately not a second endpoint. The number is a morale figure on a shift screen, not a
   * report, and a query per page load to compute it — plus the disagreement between it and the
   * list when the two are fetched a second apart — buys nothing anybody needs.
   */
  const startOfToday = useMemo(() => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);

    return midnight.getTime();
  }, []);

  const resolvedToday = all.filter(
    (row) =>
      row.status === 'RESOLVED' &&
      row.lastMessageAt !== null &&
      new Date(row.lastMessageAt).getTime() >= startOfToday,
  ).length;

  // Keeps a selection valid across a refetch, and picks the first conversation on arrival.
  useEffect(() => {
    if (rows.length === 0) {
      setSelected(null);
      return;
    }

    if (!selected || !rows.some((row) => row.id === selected)) setSelected(rows[0]!.id);
  }, [rows, selected]);

  /*
   * Opening straight onto one thread, when something sent us here with it in the URL.
   *
   * The message bell links to `?conversa=<id>`. Consumed once and removed, so the choice above is
   * free to move the selection afterwards — otherwise a reload, or a refetch, would drag the person
   * back to this thread every time.
   */
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const wanted = params.get('conversa');

    if (!wanted) return;

    setSelected(wanted);
    setParams({}, { replace: true });
  }, [params, setParams]);

  return (
    <div className="view view--wide inbox-page">
      <header className="board-head">
        <div>
          <h1>{t('inbox.pageTitle')}</h1>
          <p className="muted">{t('inbox.pageDescription')}</p>
        </div>

        {/*
         * The three numbers somebody running a shift actually watches.
         *
         * Waiting first and in amber, because it is the only one with a clock on it: a resolved
         * count is yesterday's news by comparison. They are counts of what is on this screen
         * rather than a separate report, so they cannot disagree with the list beside them.
         */}
        <div className="inbox-stats">
          <span className="inbox-stat">
            <span className="inbox-stat__dot inbox-stat__dot--waiting" aria-hidden="true" />
            <span className="inbox-stat__figure">{waiting.length}</span>
            <span className="inbox-stat__label">{t('inbox.stat.waiting')}</span>
          </span>
          <span className="inbox-stat">
            <span className="inbox-stat__dot inbox-stat__dot--handled" aria-hidden="true" />
            <span className="inbox-stat__figure">{handled.length}</span>
            <span className="inbox-stat__label">{t('inbox.stat.handled')}</span>
          </span>
          <span className="inbox-stat">
            <span className="inbox-stat__dot inbox-stat__dot--resolved" aria-hidden="true" />
            <span className="inbox-stat__figure">{resolvedToday}</span>
            <span className="inbox-stat__label">{t('inbox.stat.resolvedToday')}</span>
          </span>
        </div>

        <div className="cell-inline">
          {/* Whether you are taking conversations — which is what the queue actually routes on. */}
          <AvailabilityPicker />

          {/* The thing this screen could not do: write first. */}
          <button
            type="button"
            className="button button--primary"
            onClick={() => setStarting(true)}
          >
            + {t('inbox.newConversation')}
          </button>

          <Link to="/atendimento/canais" className="button button--quiet">
            {t('inbox.manageChannels')}
          </Link>
        </div>
      </header>

      {starting && (
        <NewConversationDialog
          channels={channels.data?.channels ?? []}
          onClose={() => setStarting(false)}
          onStarted={(conversationId) => {
            setStarting(false);
            // Straight into the thread that was just opened: nobody starts a conversation in order
            // to go back to a list.
            setSelected(conversationId);
          }}
        />
      )}

      <div className={`inbox${selected ? ' inbox--reading' : ''}`}>
        <section className="inbox__list" aria-label={t('inbox.conversations')}>
          <div className="inbox__head">
            <span className="inbox__title">
              <h2>{t('inbox.conversations')}</h2>
              <span className="inbox__count">{rows.length}</span>
            </span>

            {/*
             * Whether the screen is actually live.
             *
             * Shown because the failure it describes is invisible: a socket that has quietly died
             * looks exactly like an afternoon where nobody wrote. The list still refreshes on a
             * slow interval underneath, so this is "instant or in a minute", not "working or not".
             */}
            <span
              className={`inbox__live${connected ? ' is-live' : ''}`}
              title={connected ? t('inbox.live') : t('inbox.reconnecting')}
            >
              <span className="dot" aria-hidden="true" />
              <span className="muted small">
                {connected ? t('inbox.live') : t('inbox.reconnecting')}
              </span>
            </span>
          </div>

          <div className="inbox__filters">
            <label className="board-search">
              <SearchGlyph />
              <input
                type="search"
                value={search}
                placeholder={t('inbox.searchPlaceholder')}
                aria-label={t('inbox.searchPlaceholder')}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>

            {/* Counts on the tabs, because "is anything waiting" is the question they answer. */}
            <div className="tabs tabs--compact" role="tablist">
              {VIEWS.map((entry) => {
                const count =
                  entry.key === 'unassigned'
                    ? waiting.length
                    : entry.key === 'mine'
                      ? handled.length
                      : rows.length;

                return (
                  <button
                    key={entry.key}
                    type="button"
                    role="tab"
                    aria-selected={view === entry.key}
                    className="tab"
                    onClick={() => setView(entry.key)}
                  >
                    {t(entry.labelKey)}
                    <span className="tab__count">{count}</span>
                  </button>
                );
              })}
            </div>

            {/*
             * The channels, as chips with their names on.
             *
             * This was a select and a row of unlabelled icons, which is two controls for one
             * decision and neither of them readable at a glance. A chip carries the mark and the
             * name, and "Todos" is one of them rather than an empty option.
             */}
            <div className="channel-chips" role="group" aria-label={t('inbox.filterChannel')}>
              <button
                type="button"
                className={`channel-chip${channelId === '' ? ' is-active' : ''}`}
                aria-pressed={channelId === ''}
                onClick={() => setChannelId('')}
              >
                {t('inbox.allChannels')}
              </button>

              {(channels.data?.channels ?? []).map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={`channel-chip${channelId === entry.id ? ' is-active' : ''}`}
                  aria-pressed={channelId === entry.id}
                  onClick={() => setChannelId(channelId === entry.id ? '' : entry.id)}
                >
                  <ChannelMark kind={entry.kind} />
                  <span>{entry.name}</span>
                </button>
              ))}
            </div>

            <select
              className="select"
              value={status}
              onChange={(event) => setStatus(event.target.value as ConversationStatus | 'ALL')}
              aria-label={t('inbox.filterStatus')}
            >
              {STATUSES.map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {t(entry.labelKey)}
                </option>
              ))}
            </select>
          </div>

          {/*
           * An error replaces the list only when there is no list.
           *
           * The same rule the thread got in Phase 82, for the same reason and against the same
           * screenshot: an API restart or a blink of wifi replaced the agent's queue with "não foi
           * possível carregar" while the conversations it had were still perfectly true. The
           * connection dot at the top already says the tab is reconnecting; the work does not need
           * to disappear to make that point.
           */}
          {conversations.error && rows.length === 0 ? (
            <ErrorState error={conversations.error} onRetry={() => void conversations.refetch()} />
          ) : conversations.isLoading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <EmptyState title={t('inbox.emptyTitle')} description={t('inbox.emptyBody')} />
          ) : (
            <div className="inbox__groups">
              <Group
                labelKey="inbox.group.handled"
                rows={handled}
                selected={selected}
                kindOf={kindOf}
                onSelect={setSelected}
              />
              <Group
                labelKey="inbox.group.waiting"
                rows={waiting}
                selected={selected}
                kindOf={kindOf}
                waiting
                onSelect={setSelected}
              />
            </div>
          )}
        </section>

        <section className="inbox__thread" aria-label={t('inbox.thread')}>
          {selected ? (
            <Thread
              key={selected}
              conversationId={selected}
              onBack={() => setSelected(null)}
              onMoved={setSelected}
            />
          ) : (
            <EmptyState title={t('inbox.pickTitle')} description={t('inbox.pickBody')} />
          )}
        </section>
      </div>
    </div>
  );
}

/** One band of the list. Renders nothing at all when it holds nothing — an empty heading is noise. */
function Group({
  labelKey,
  rows,
  selected,
  kindOf,
  waiting,
  onSelect,
}: {
  labelKey: string;
  rows: readonly ConversationSummary[];
  selected: string | null;
  kindOf: (channelId: string) => ChannelKind;
  waiting?: boolean;
  onSelect: (id: string) => void;
}): React.JSX.Element | null {
  if (rows.length === 0) return null;

  return (
    <>
      <p className="inbox__group">
        <span className="strong">{t(labelKey)}</span>
        <span className="inbox__count">{rows.length}</span>
      </p>

      <ul className="conversation-list">
        {rows.map((conversation) => (
          <li key={conversation.id}>
            <ConversationRow
              conversation={conversation}
              kind={kindOf(conversation.channelId)}
              active={conversation.id === selected}
              waiting={waiting ?? false}
              onSelect={() => onSelect(conversation.id)}
            />
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * One conversation in the list.
 *
 * The channel rides on the avatar rather than in a column, because it is the first thing an agent
 * reads and the last thing that should cost a glance. A waiting thread shows **how long** it has
 * been waiting instead of when it last moved: on the ones nobody has picked up, the age is the
 * whole point.
 */
function ConversationRow({
  conversation,
  kind,
  active,
  waiting,
  onSelect,
}: {
  conversation: ConversationSummary;
  kind: ChannelKind;
  active: boolean;
  waiting: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  const breached =
    conversation.sla.firstResponse === 'BREACHED' || conversation.sla.resolution === 'BREACHED';

  const name = conversation.peerName ?? conversation.peerAddress;
  const minutes = minutesSince(conversation.lastMessageAt);

  return (
    <button
      type="button"
      className={`conversation${active ? ' conversation--active' : ''}`}
      onClick={onSelect}
      aria-current={active}
    >
      <span className="conversation__avatar">
        {/* The customer's own picture where the provider gave one. It is a short-lived URL, and
            `Avatar` falls back to initials the moment it stops loading. */}
        <StoredAvatar
          name={name}
          fileId={conversation.peerAvatarFileId}
          url={conversation.peerAvatarUrl}
          size="sm"
        />
        <span className="conversation__channel">
          <ChannelMark kind={kind} />
        </span>
      </span>

      <span className="conversation__body">
        <span className="conversation__top">
          <span className="conversation__name">{name}</span>

          {waiting && minutes !== null ? (
            <span className={`conversation__waited${breached ? ' is-late' : ''}`}>
              {t('inbox.waited').replace('{n}', String(minutes))}
            </span>
          ) : (
            <span className="conversation__when">{formatRelative(conversation.lastMessageAt)}</span>
          )}
        </span>

        <span className="conversation__preview muted">{conversation.peerAddress}</span>

        {/*
          The tags, on the row rather than one click inside it.

          A queue is triaged by scanning it, and the labels a team puts on a conversation — the plan
          somebody is on, whether they are a reseller, what went wrong — are what that scan is
          looking for. Capped at three with a count for the rest, because a row that grows with the
          number of tags stops being scannable, which is the thing it was for.
        */}
        {conversation.tags.length > 0 && (
          <span className="conversation__tags">
            {conversation.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="chip chip--tag" title={tag}>
                {tag}
              </span>
            ))}
            {conversation.tags.length > 3 && (
              <span className="chip chip--tag chip--more">+{conversation.tags.length - 3}</span>
            )}
          </span>
        )}

        <span className="conversation__bottom">
          <span className="muted small">{t(`channels.kind.${kind}`)}</span>

          <span className="conversation__marks">
            {/* Open or closed, said plainly: it was legible only by its absence before. */}
            <Badge tone={STATUS_TONE[conversation.status] ?? 'neutral'}>
              {t(`inbox.status.${conversation.status}`)}
            </Badge>
            {breached && <Badge tone="danger">{t('inbox.sla.breached')}</Badge>}
            {conversation.unreadCount > 0 && (
              <span className="conversation__unread">{conversation.unreadCount}</span>
            )}
          </span>
        </span>
      </span>
    </button>
  );
}

/**
 * What each conversation status looks like at a glance.
 *
 * Open is the working state and reads as neutral-positive; resolved is done; pending and snoozed
 * are both "not now", and are warned about rather than celebrated.
 */
const STATUS_TONE: Record<string, BadgeTone> = {
  OPEN: 'info',
  PENDING: 'warning',
  SNOOZED: 'warning',
  RESOLVED: 'positive',
};

/** Whole minutes since something happened, or null when it never has. */
function minutesSince(at: string | null): number | null {
  if (!at) return null;

  return Math.max(0, Math.round((Date.now() - new Date(at).getTime()) / 60_000));
}

function Thread({
  conversationId,
  onBack,
  onMoved,
}: {
  conversationId: string;
  onBack: () => void;
  /** Where the thread went, when two were joined into one under it (Phase 81). */
  onMoved: (id: string) => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const bottom = useRef<HTMLDivElement>(null);

  const [draft, setDraft] = useState('');
  const [transferOpen, setTransferOpen] = useState(false);
  /*
   * The contact panel starts closed (Phase 144).
   *
   * The thread is what somebody came for, and a column of facts they already know was taking a
   * third of the width. It lives here rather than inside the panel so the panel can be unmounted
   * from the layout without the thread ever re-rendering from scratch.
   */
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [isPrivate, setIsPrivate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The message being replied to, when the agent picked one (Phase 106). */
  const [replyingTo, setReplyingTo] = useState<MessageView | null>(null);
  /**
   * Whether this reply carries the sender's name (Phase 113).
   *
   * `null` means "whatever the desk decided", which is what the server does with an absent value.
   * A person switching it changes it for the messages they write from now on, not for the desk.
   */
  const [signing, setSigning] = useState<boolean | null>(null);
  /** Which quick reply was inserted, so its use is counted once it is actually sent (Phase 114). */
  const [usedQuickReply, setUsedQuickReply] = useState<string | null>(null);
  /** Which row of the picker is highlighted. */
  const [picked, setPicked] = useState(0);
  /** When the draft should be sent instead of now, while the agent is choosing (Phase 115). */
  const [scheduleAt, setScheduleAt] = useState<string | null>(null);
  /*
   * Up here with the others, and the reason is a crash.
   *
   * This lived two hundred lines below, **after** `if (!detail.data) return <Loading />` — so the
   * first render of a thread called 58 hooks and the second called 59, and React threw "Rendered
   * more hooks than during the previous render". Every switch to a conversation whose thread was
   * not already cached blew up into the error boundary, which is what an agent saw as "erro ao
   * carregar, tente de novo" each time they clicked a different customer.
   *
   * Nothing warned, because this repository had no `react-hooks` lint rule until now.
   */
  const [offering, setOffering] = useState(false);

  const detail = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => getConversation(conversationId),
    // Same rule as the list: the socket is the path, the interval is the floor.
    refetchInterval: 60_000,
  });

  const refreshThread = useCallback(() => {
    void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
    void client.invalidateQueries({ queryKey: ['window', conversationId] });
  }, [client, conversationId]);

  /*
   * A delivery receipt moves the tick in place, without a round trip (Phase 85).
   *
   * Everything else on this topic means "something changed, read it again", and that is right for a
   * message arriving. A receipt is different: it is one field of one message, it arrives a second
   * after the agent pressed send, and re-reading the whole thread to draw a tick is both slower and
   * more work than the event itself carries.
   *
   * The refetch still happens behind it, so an event that raced a reload cannot leave the cache
   * saying something the server does not.
   */
  const applyDelivery = useCallback(
    (payload: { message_id?: string; status?: string; failure_reason?: string }) => {
      if (!payload.message_id || !payload.status) return;

      client.setQueryData<ConversationDetail>(['conversation', conversationId], (current) =>
        current
          ? {
              ...current,
              messages: current.messages.map((message) =>
                message.id === payload.message_id
                  ? {
                      ...message,
                      status: payload.status as MessageView['status'],
                      failureReason: payload.failure_reason ?? message.failureReason,
                    }
                  : message,
              ),
            }
          : current,
      );
    },
    [client, conversationId],
  );

  // Only this thread's topic: waking every agent for every message in every conversation is how a
  // real-time feature becomes the reason somebody turns it off.
  useRealtimeTopic(`inbox:${conversationId}`, (event) => {
    if (event.type === 'message.delivery') {
      applyDelivery(event.payload as { message_id?: string; status?: string });

      return;
    }

    refreshThread();
  });
  useRealtimeReconnect(refreshThread);

  const window_ = useQuery({
    queryKey: ['window', conversationId],
    queryFn: () => getSessionWindow(conversationId),
    // A conversation on a channel without a window answers this too; a failure is not fatal here.
    retry: false,
  });

  /*
   * Two threads that became one.
   *
   * When WhatsApp reveals that a LID and a number are the same person the inbox merges the threads,
   * and the API answers this id with the surviving one. Following it here is what keeps an agent
   * reading the same customer instead of a "não encontrada" — and what makes the reply go to the
   * thread that is actually open.
   */
  const movedTo = detail.data?.id;

  useEffect(() => {
    if (movedTo && movedTo !== conversationId) onMoved(movedTo);
  }, [movedTo, conversationId, onMoved]);

  const messages = useMemo(() => detail.data?.messages ?? [], [detail.data?.messages]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const reply = useMutation({
    mutationFn: () =>
      sendReply(conversationId, {
        body: draft.trim(),
        isPrivate,
        ...(attachments.length > 0
          ? { media: attachments.map(({ label: _label, uploading: _u, ...rest }) => rest) }
          : {}),
        // A note is for colleagues; quoting the customer in one would send nothing anywhere.
        ...(replyingTo && !isPrivate ? { repliedToId: replyingTo.id } : {}),
        ...(signing === null || isPrivate ? {} : { sign: signing }),
      }),
    onSuccess: () => {
      // Counted here, not when it was inserted: a draft somebody rewrote was never sent.
      if (usedQuickReply) void noteQuickReplyUse(usedQuickReply).catch(() => undefined);

      setDraft('');
      setAttachments([]);
      setReplyingTo(null);
      setUsedQuickReply(null);
      setError(null);
      void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void client.invalidateQueries({ queryKey: ['inbox'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  /*
   * What the desk decided about signatures, so the switch starts where it should.
   *
   * Cached for the session: it is a setting, not a feed, and a reply must not wait on it.
   */
  const tenant = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: getTenantSettings,
    staleTime: 10 * 60_000,
    retry: false,
  });

  /*
   * The answers this person may send, filled in for this conversation (Phase 114).
   *
   * Per conversation rather than once for the screen, because `{contato}` is this customer's name:
   * a list cached across threads would offer to greet one person by another's name.
   */
  const quickReplies = useQuery({
    queryKey: ['quick-replies', conversationId],
    queryFn: () => listQuickReplies(conversationId),
    staleTime: 60_000,
    retry: false,
  });

  /**
   * What the agent is reaching for, when the draft is a shortcut and nothing else (Phase 114).
   *
   * Only at the very start of an otherwise empty draft: a "/" in the middle of a sentence is a
   * date, a fraction or a URL, and a picker that opened there would fight the person writing.
   */
  const picking = ((): QuickReply[] => {
    const match = /^\/([a-z0-9_-]*)$/i.exec(draft);

    if (!match || isPrivate) return [];

    const typed = match[1]!.toLowerCase();

    return (quickReplies.data?.quick_replies ?? [])
      .filter((item) => item.shortcut.startsWith(typed))
      .slice(0, 6);
  })();

  /** Puts the answer in the box, ready to be read before it is sent. */
  const insertQuickReply = (item: QuickReply): void => {
    setDraft(item.body);
    setUsedQuickReply(item.id);
    setPicked(0);
  };

  /** What is already waiting on this thread, so nobody promises the same thing twice. */
  const scheduled = useQuery({
    queryKey: ['scheduled', conversationId],
    queryFn: () => listScheduled(conversationId),
    staleTime: 30_000,
    retry: false,
  });

  const schedule = useMutation({
    mutationFn: () =>
      scheduleMessage(conversationId, {
        body: draft.trim(),
        sendAt: new Date(scheduleAt!).toISOString(),
        ...(attachments.length > 0
          ? { media: attachments.map(({ label: _label, uploading: _u, ...rest }) => rest) }
          : {}),
      }),
    onSuccess: () => {
      setDraft('');
      setAttachments([]);
      setScheduleAt(null);
      setError(null);
      void client.invalidateQueries({ queryKey: ['scheduled', conversationId] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const unschedule = useMutation({
    mutationFn: (id: string) => cancelScheduled(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['scheduled', conversationId] }),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels, staleTime: 60_000 });
  const channel = channels.data?.channels.find((row) => row.id === detail.data?.channelId);

  const handling = useMutation({
    mutationFn: (next: 'BOT' | 'HUMAN') => setConversationHandling(conversationId, next),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void client.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const resolve = useMutation({
    mutationFn: (next: ConversationStatus) =>
      setConversationStatus(conversationId, { status: next }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void client.invalidateQueries({ queryKey: ['inbox'] });
    },
  });

  /*
   * An error replaces the thread only when there is no thread.
   *
   * A refetch that fails — a token being renewed, a blink of wifi — used to blank a conversation an
   * agent was in the middle of reading and replace it with "tente de novo". What is already on
   * screen is still true; the reload will come round again.
   */
  if (detail.error && !detail.data) {
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  }

  if (!detail.data) return <Loading />;

  const conversation = detail.data;

  /*
   * The 24-hour window is the **official** route's rule (Phase 84).
   *
   * A QR-paired number has no window and no templates: it is somebody's own WhatsApp and it sends
   * what a person could send, whenever a person could. Telling an agent there that "a janela de 24
   * horas fechou. Fora dela o WhatsApp só entrega um template aprovado pela Meta" describes a
   * restriction that does not exist, and it disabled the box they were trying to type in.
   *
   * The server answers `open: true` for those channels now; this is the second lock on the same
   * door, so a stale cached answer cannot close a composer that should never be closed.
   *
   * Written as an exception, like the server's rule: a WhatsApp provider this build has not heard
   * of keeps Meta's restriction, because sending into a closed window costs a number its rating and
   * being asked for a template costs a click.
   */
  const metaRules = channel?.kind === 'WHATSAPP' && channel.provider !== 'whatsapp_web';

  // A note is always allowed: it is written into the thread and never delivered to anybody.
  const windowClosed = metaRules && window_.data ? !window_.data.open : false;
  const canWriteFreely = isPrivate || !windowClosed;

  // A picture with nothing to say is a message; an empty box is not.
  const sendable = draft.trim() !== '' || attachments.length > 0;

  const peerName = conversation.peerName ?? conversation.peerAddress;

  return (
    <div className={`thread${detailsOpen ? ' thread--with-details' : ''}`}>
      <div className="thread__main">
        <header className="thread__header">
          <button
            type="button"
            className="thread__back"
            onClick={onBack}
            aria-label={t('inbox.back')}
          >
            ←
          </button>

          {/*
           * The whole identification is the way in (Phase 144).
           *
           * A button rather than an icon beside the name: "clicar no cliente para ver o cliente"
           * is the gesture people already try, and an icon is a thing they have to find first.
           * Everything else in this bar is an action, so only this opens the panel.
           */}
          <button
            type="button"
            className="thread__identity"
            aria-expanded={detailsOpen}
            aria-label={t('inbox.openDetails')}
            onClick={() => setDetailsOpen((current) => !current)}
          >
            <StoredAvatar
              name={peerName}
              fileId={conversation.peerAvatarFileId}
              url={conversation.peerAvatarUrl}
              size="sm"
            />

            <span className="thread__who">
              <span className="thread__title">{peerName}</span>
              <span className="muted small thread__where">
                {channel && <ChannelMark kind={channel.kind} />}
                {channel ? t(`channels.kind.${channel.kind}`) : conversation.peerAddress}
                {' · '}
                {t(`inbox.status.${conversation.status}`)}
              </span>
            </span>
          </button>

          <div className="thread__actions">
            {/*
             * What an agent starts from here (Phase 146).
             *
             * Four dialogs — a deal, a task, the summary, the sales suggestion — because all four
             * happen *during* a conversation: sending somebody to the pipeline to raise a deal
             * means leaving the thread, losing the draft, and coming back to find the customer
             * wrote again.
             */}
            <ThreadTools
              conversationId={conversation.id}
              contactId={conversation.contactId}
              contactName={conversation.peerName ?? conversation.peerAddress}
            />

            {/*
             * Handing the thread on, from where the thread is.
             *
             * The same two operations the side panel offers, brought up here because passing a
             * conversation to a colleague or another queue is something an agent does constantly
             * and it sat behind opening a panel first.
             */}
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setTransferOpen(true)}
            >
              {t('inbox.transfer.title')}
            </button>

            <TransferDialog
              open={transferOpen}
              conversationId={conversation.id}
              assigneeId={conversation.assigneeId}
              queueId={conversation.queueId}
              onClose={() => setTransferOpen(false)}
            />

            {/*
             * Who is answering. The badge is the whole point of Phase 24 on this screen: a person
             * must be able to see at a glance whether the bot is talking to this customer, and take
             * the thread with one click when it is not going well. Handing it back is a separate,
             * deliberate button — the bot never resumes on its own.
             */}
            {conversation.handling !== 'HUMAN' && (
              <Badge tone={conversation.handling === 'BOT' ? 'info' : 'warning'}>
                {t(`inbox.handling.${conversation.handling}`)}
              </Badge>
            )}

            {conversation.handling !== 'HUMAN' ? (
              <button
                type="button"
                className="button button--primary button--compact"
                onClick={() => handling.mutate('HUMAN')}
                disabled={handling.isPending}
                title={conversation.handoffReason ?? undefined}
              >
                {t('inbox.takeOver')}
              </button>
            ) : (
              channel?.botAgentId && (
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => handling.mutate('BOT')}
                  disabled={handling.isPending}
                >
                  {t('inbox.handBack')}
                </button>
              )
            )}

            <Badge tone={conversation.status === 'RESOLVED' ? 'positive' : 'info'}>
              {t(`inbox.status.${conversation.status}`)}
            </Badge>

            {conversation.status !== 'RESOLVED' ? (
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => resolve.mutate('RESOLVED')}
                disabled={resolve.isPending}
              >
                {t('inbox.resolve')}
              </button>
            ) : (
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => resolve.mutate('OPEN')}
                disabled={resolve.isPending}
              >
                {t('inbox.reopen')}
              </button>
            )}
          </div>
        </header>

        <div className="thread__messages">
          {messages.length === 0 ? (
            <EmptyState title={t('inbox.noMessages')} />
          ) : (
            messages.map((message, index) => (
              <Fragment key={message.id}>
                {/*
                 * A date, once, where the day changes.
                 *
                 * A thread read from the top is a sequence of times with no anchor; "14:32" is
                 * meaningless three days later. The divider only appears where the day actually
                 * turns, so an afternoon of messages has one, not forty.
                 */}
                {startsNewDay(messages[index - 1], message) && (
                  <p className="thread__day">
                    <span>{dayLabel(message.createdAt)}</span>
                  </p>
                )}

                <Bubble message={message} conversationId={conversationId} onReply={setReplyingTo} />
              </Fragment>
            ))
          )}
          <div ref={bottom} />
        </div>

        <footer className={`composer${isPrivate ? ' composer--note' : ''}`}>
          {windowClosed && !isPrivate && (
            <p className="composer__warning" role="status">
              {t('inbox.windowClosed')}
              {window_.data && window_.data.templates.length > 0 && (
                <TemplateSender
                  conversationId={conversationId}
                  templates={window_.data.templates}
                  onSent={() => {
                    void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
                    void client.invalidateQueries({ queryKey: ['window', conversationId] });
                  }}
                />
              )}
            </p>
          )}

          {!windowClosed && window_.data?.minutes_left !== null && window_.data && (
            <p className="composer__hint muted small">
              {t('inbox.windowOpen').replace('{minutes}', String(window_.data.minutes_left ?? 0))}
            </p>
          )}

          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}

          {/*
           * What is being replied to, above the box somebody is typing in (Phase 106).
           *
           * With a way out: picking the wrong message is one click, and a reply that quotes the
           * wrong thing is worse than one that quotes nothing. A note cannot carry a quote — it
           * never reaches WhatsApp — so switching to the note tab clears it rather than sending
           * something that would be silently dropped.
           */}
          {/*
           * What this thread has already promised (Phase 115).
           *
           * Above the composer and not in a panel somebody has to open: the reason to show it is
           * so nobody writes the same follow-up twice, and a list you have to go looking for does
           * not prevent that.
           */}
          {(scheduled.data?.scheduled?.length ?? 0) > 0 && (
            <ul className="composer__scheduled">
              {(scheduled.data?.scheduled ?? []).map((item) => (
                <li key={item.id}>
                  <span className="muted small">
                    {t('inbox.scheduledFor').replace('{when}', formatDateTime(item.sendAt))}
                  </span>
                  <span className="composer__scheduled-body">
                    {item.body || t('inbox.attachment')}
                  </span>
                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    onClick={() => unschedule.mutate(item.id)}
                    disabled={unschedule.isPending}
                  >
                    {t('confirm.cancel')}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {replyingTo && !isPrivate && (
            <p className="composer__quote">
              <span className="composer__quote-text">
                {replyingTo.body.trim() || t('inbox.attachment')}
              </span>

              <button
                type="button"
                className="composer__quote-clear"
                onClick={() => setReplyingTo(null)}
                aria-label={t('confirm.cancel')}
              >
                ×
              </button>
            </p>
          )}

          {/*
           * Reply or note, as two tabs rather than a checkbox.
           *
           * They are two different acts — one leaves the building and one does not — and a tick box
           * beside a send button is the wrong weight for that difference. The composer changes
           * colour with the tab, so the state is visible while somebody is typing rather than only
           * where the control is.
           */}
          <div className="composer__row">
            <div className="tabs tabs--compact" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={!isPrivate}
                className="tab"
                onClick={() => setIsPrivate(false)}
              >
                {t('inbox.reply')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isPrivate}
                className="tab"
                onClick={() => setIsPrivate(true)}
              >
                {t('inbox.privateNote')}
              </button>
            </div>

            {/*
             * Signing this reply, or not (Phase 113).
             *
             * Beside the composer rather than only in settings, because the one reply that should
             * not carry a name is usually the one being written right now — a correction, or a
             * message a colleague asked to be sent on their behalf. Untouched, it follows whatever
             * the desk decided, which is what the server does with an absent value.
             */}
            {!isPrivate && (
              <label className="composer__sign">
                <input
                  type="checkbox"
                  checked={signing ?? tenant.data?.inboxSignature?.enabled ?? false}
                  onChange={(event) => setSigning(event.target.checked)}
                />
                <span className="muted small">{t('inbox.sign')}</span>
              </label>
            )}

            {/* Which way a reply actually leaves. On a thread that has two, saying so is not decoration. */}
            {channel && !isPrivate && (
              <span className="composer__via muted small">
                <ChannelMark kind={channel.kind} />
                {t('inbox.via').replace('{channel}', t(`channels.kind.${channel.kind}`))}
              </span>
            )}

            {/* Only while the window is open and only on a channel that has them: there is no
              approved-template equivalent for an interactive message, so offering the affordance
              afterwards would be offering a message that cannot be sent. */}
            {/* Interactive messages are an official-API message type; the QR route has none. */}
            {!isPrivate && !windowClosed && metaRules && (
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setOffering((current) => !current)}
              >
                {offering ? t('inbox.quickRepliesClose') : t('inbox.quickReplies')}
              </button>
            )}
          </div>

          {offering && (
            <QuickReplyComposer
              conversationId={conversationId}
              onSent={() => {
                setOffering(false);
                void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
              }}
              onError={setError}
            />
          )}

          {/* What is going with the message, before it goes. */}
          <AttachmentTray
            items={attachments}
            onRemove={(fileId) =>
              setAttachments((current) => current.filter((item) => item.fileId !== fileId))
            }
          />

          <div className="composer__row composer__row--typing">
            {/*
             * The answers this desk sends every day, reached by typing "/" (Phase 114).
             *
             * A list that appears while typing rather than a panel to open: the point of a canned
             * answer is not lifting your hands off the keyboard. Enter takes the highlighted one,
             * Escape puts the slash back to being a slash.
             */}
            {picking.length > 0 && (
              <ul className="composer__picks" role="listbox">
                {picking.map((item, index) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`composer__pick${index === picked ? ' composer__pick--on' : ''}`}
                      onMouseEnter={() => setPicked(index)}
                      onClick={() => insertQuickReply(item)}
                    >
                      <span className="strong">/{item.shortcut}</span>
                      <span className="muted small">{item.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <textarea
              value={draft}
              rows={3}
              placeholder={isPrivate ? t('inbox.notePlaceholder') : t('inbox.replyPlaceholder')}
              disabled={!canWriteFreely}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                /*
                 * The picker owns these keys while it is open.
                 *
                 * Otherwise Enter would send "/hor" to a customer — which is the failure mode of
                 * every half-built command palette.
                 */
                if (picking.length > 0) {
                  if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    setPicked((current) => (current + 1) % picking.length);

                    return;
                  }

                  if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    setPicked((current) => (current - 1 + picking.length) % picking.length);

                    return;
                  }

                  if (event.key === 'Enter' || event.key === 'Tab') {
                    event.preventDefault();
                    insertQuickReply(picking[picked] ?? picking[0]!);

                    return;
                  }

                  if (event.key === 'Escape') {
                    event.preventDefault();
                    setPicked(0);
                    setDraft(`${draft} `);

                    return;
                  }
                }

                // Enter sends, Shift+Enter breaks the line: what every messaging tool does.
                if (event.key === 'Enter' && !event.shiftKey && sendable && canWriteFreely) {
                  event.preventDefault();
                  reply.mutate();
                }
              }}
            />

            <div className="composer__tools">
              {/*
               * Sending this later, behind a clock (Phase 146).
               *
               * The date field used to sit here all day for the one message a week that is
               * scheduled, taking the width of two buttons to do nothing.
               */}
              {!isPrivate && (
                <ScheduleButton
                  disabled={!canWriteFreely || !sendable}
                  value={scheduleAt}
                  onChange={setScheduleAt}
                  onSchedule={() => schedule.mutate()}
                  pending={schedule.isPending}
                />
              )}

              {/* Correcting and rewriting: two requests, two buttons, and neither of them sends. */}
              {!isPrivate && (
                <PolishButtons
                  draft={draft}
                  disabled={!canWriteFreely || reply.isPending}
                  onReplace={setDraft}
                />
              )}

              {/* A note is written into the thread and never delivered, so it carries no file. */}
              {!isPrivate && (
                <>
                  <AttachButton
                    disabled={!canWriteFreely || reply.isPending}
                    onAttached={(item) => setAttachments((current) => [...current, item])}
                    onError={setError}
                  />
                  <RecordButton
                    disabled={!canWriteFreely || reply.isPending}
                    onRecorded={(item) => setAttachments((current) => [...current, item])}
                    onError={setError}
                  />
                </>
              )}

              <button
                type="button"
                className="button button--primary button--compact"
                disabled={!canWriteFreely || !sendable || reply.isPending}
                onClick={() => reply.mutate()}
              >
                {reply.isPending ? t('inbox.sending') : t('inbox.send')}
              </button>
            </div>
          </div>
        </footer>
      </div>

      {/*
       * Who is on the other end — closed until somebody asks (Phase 144).
       *
       * A sibling of the thread rather than something inside it, so opening it cannot cost the
       * messages, the scroll position or a half-typed reply.
       */}
      <ContactDrawer
        conversation={conversation}
        channelKind={channel?.kind ?? null}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />
    </div>
  );
}

/** Whether a divider belongs above this message. */
function startsNewDay(previous: MessageView | undefined, message: MessageView): boolean {
  if (!previous) return true;

  return new Date(previous.createdAt).toDateString() !== new Date(message.createdAt).toDateString();
}

/** "Hoje", "Ontem", or the date written out. */
function dayLabel(iso: string): string {
  const at = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);

  yesterday.setDate(today.getDate() - 1);

  if (at.toDateString() === today.toDateString()) return t('inbox.today');
  if (at.toDateString() === yesterday.toDateString()) return t('inbox.yesterday');

  return at.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Just the clock, because the day is on the divider above. */
function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Delivery, in the notation everybody already reads.
 *
 * One tick left the product, two arrived, two in colour were read. Nothing at all means it has not
 * left yet — which is the state that matters most and the one a word would bury in a row of them.
 */
function DeliveryTicks({ status, label }: { status: string; label: string }): React.JSX.Element {
  const arrived = status === 'DELIVERED' || status === 'READ';
  const read = status === 'READ';

  if (status === 'PENDING') {
    return (
      <span className="ticks ticks--pending" title={label} aria-label={label}>
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 5v3.2l2 1.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </span>
    );
  }

  return (
    <span className={`ticks${read ? ' ticks--read' : ''}`} title={label} aria-label={label}>
      <svg viewBox="0 0 20 16" width="18" height="14" aria-hidden="true">
        <path
          d="M1.5 8.5 5 12l6.5-8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {arrived && (
          <path
            d="M7.5 8.5 11 12l6.5-8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </span>
  );
}

function Bubble({
  message,
  conversationId,
  onReply,
}: {
  message: MessageView;
  conversationId: string;
  /** Picks this message as the one being replied to; absent for a message nobody can quote. */
  onReply?: (message: MessageView) => void;
}): React.JSX.Element {
  const client = useQueryClient();
  const mine = message.direction === 'OUTBOUND';

  /*
   * Two taps rather than a dialog (Phase 107).
   *
   * Taking a message off somebody's phone is not undoable, so it is not one click; and a browser
   * confirm would be a modal over a thread an agent is reading. The second tap says what it does.
   */
  const [confirming, setConfirming] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: () => deleteForCustomer(conversationId, message.id),
    onSuccess: () => {
      setConfirming(false);
      setRemoveError(null);
      void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
    },
    onError: (caught: unknown) => {
      setConfirming(false);
      // WhatsApp's own refusal — its window for taking a message back — arrives here as words.
      setRemoveError(messageFor(caught));
    },
  });

  const options = message.interactive
    ? message.interactive.kind === 'BUTTONS'
      ? message.interactive.buttons
      : message.interactive.sections.flatMap((section) => section.rows)
    : [];

  return (
    <article
      className={`bubble${mine ? ' bubble--out' : ''}${message.isPrivate ? ' bubble--note' : ''}`}
    >
      {message.isPrivate && <span className="bubble__tag">{t('inbox.note')}</span>}

      {/*
       * Who answered, when it was not a person (Phase 110).
       *
       * A desk running an SDR bot and a support bot on the same number cannot otherwise tell whose
       * words the customer is reacting to — and neither can the colleague who has to answer next.
       * Named rather than a generic "IA": which agent it was is the part that decides what to do.
       */}
      {message.authorType === 'AI_AGENT' && (
        <span className="bubble__tag bubble__tag--ai">
          {message.authorName
            ? t('inbox.byAgent').replace('{name}', message.authorName)
            : t('inbox.byAgentUnknown')}
        </span>
      )}

      {/*
       * And who answered, when it was a colleague (Phase 113).
       *
       * A shift handing over cannot otherwise tell which of three people promised what — the
       * thread held the author all along and simply never drew it. Only on the account's own
       * messages: the customer's name is already at the top of the screen.
       */}
      {message.authorType === 'USER' && message.authorName && !message.isPrivate && (
        <span className="bubble__tag bubble__tag--who">{message.authorName}</span>
      )}

      {/* The way to answer this one in particular. Hidden until the bubble is hovered or focused,
          so a thread reads as a conversation rather than as a row of controls. */}
      {onReply && !message.isPrivate && !message.deletedAt && (
        <button
          type="button"
          className="bubble__reply"
          onClick={() => onReply(message)}
          title={t('inbox.replyTo')}
          aria-label={t('inbox.replyTo')}
        >
          ↩
        </button>
      )}

      {/*
       * Taking a message back, offered only where it can work (Phase 107).
       *
       * This account's own message, actually delivered, not a note, not already withdrawn.
       * WhatsApp still has the last word — its own rule about how long — and its refusal is shown
       * as the error it is.
       */}
      {mine && !message.isPrivate && !message.deletedAt && message.status !== 'PENDING' && (
        <button
          type="button"
          className={`bubble__delete${confirming ? ' bubble__delete--confirming' : ''}`}
          onClick={() => (confirming ? remove.mutate() : setConfirming(true))}
          onBlur={() => setConfirming(false)}
          disabled={remove.isPending}
          title={confirming ? t('inbox.deleteConfirm') : t('inbox.deleteForCustomer')}
          aria-label={confirming ? t('inbox.deleteConfirm') : t('inbox.deleteForCustomer')}
        >
          {confirming ? t('inbox.deleteConfirmShort') : '🗑'}
        </button>
      )}

      {removeError && (
        <span className="bubble__failure" role="alert">
          {removeError}
        </span>
      )}

      {/* A tap, drawn as the answer it was rather than as a sentence that happens to match one. */}
      {message.replyToOption && <span className="bubble__tag">{t('inbox.tapped')}</span>}

      {/*
       * What this message answers (Phase 106).
       *
       * Drawn inside the bubble and above the text, the way every messenger draws it, because a
       * reply read without the thing it replies to is half a sentence. The excerpt comes from the
       * server already trimmed: a quote is a pointer, not a second copy of the message.
       */}
      {message.repliedTo && (
        <span className="bubble__quote">
          <span className="bubble__quote-who">
            {message.repliedTo.direction === 'OUTBOUND'
              ? t('inbox.quoteMine')
              : t('inbox.quoteTheirs')}
          </span>
          <span className="bubble__quote-text">
            {message.repliedTo.excerpt || (message.repliedTo.hasMedia ? t('inbox.attachment') : '')}
          </span>
        </span>
      )}

      {/* The picture before the caption, which is the order WhatsApp shows them in and the order
          somebody reads them in. */}
      {!message.deletedAt && (
        <MessageMedia
          media={message.media}
          outbound={mine}
          conversationId={conversationId}
          messageId={message.id}
        />
      )}

      {/*
       * A message the agent took back (Phase 107).
       *
       * Said where the words were, rather than removed: an agent scrolling back needs to know that
       * something was sent and withdrawn, otherwise the customer's next sentence answers nothing.
       */}
      {message.deletedAt ? (
        <p className="bubble__body bubble__body--deleted">{t('inbox.deleted')}</p>
      ) : (
        (message.body || message.interactive) && (
          <p className="bubble__body">
            {message.interactive ? message.interactive.body : message.body}
          </p>
        )
      )}

      {/* What the customer was offered, as the customer saw it: a list of options, not a paragraph
          with bullets in it. An agent scrolling back needs this to read the answer. */}
      {options.length > 0 && (
        <ul className="bubble__options">
          {options.map((option) => (
            <li key={option.id}>{option.title}</li>
          ))}
        </ul>
      )}

      <p className="bubble__meta">
        <span>{clockTime(message.createdAt)}</span>

        {/*
         * Ticks rather than a word.
         *
         * This is the vocabulary every person using WhatsApp already reads without thinking, and
         * it takes a corner of the bubble instead of a line under it. A failure is the exception
         * and still says so in words, because a customer not receiving a message is not something
         * to encode in a glyph.
         */}
        {mine && (message.status === 'PENDING' || !message.failureReason) && (
          <DeliveryTicks status={message.status} label={t(`inbox.delivery.${message.status}`)} />
        )}

        {/*
         * A failure an agent can read, and undo.
         *
         * This used to print the provider's own words in English — a real thread ended with
         * `session service answered 500: {"error":"t"}` under a voice note somebody had just
         * recorded, and their only option was to record it again. The original is kept in the
         * tooltip, where support needs it and the agent does not.
         */}
        {message.failureReason &&
          (message.status === 'PENDING' ? (
            /*
             * Still on its way, and saying why it is taking a while (Phase 105).
             *
             * A message whose channel is reconnecting keeps its place in the queue instead of
             * failing — so the bubble must not draw the red line and the try-again button for
             * something nobody needs to rescue. A clock with no explanation is what produced
             * "toda hora fica com o reloginho" in the first place; this is the explanation.
             */
            <span className="bubble__waiting" title={message.failureReason}>
              {t('inbox.waitingForChannel')}
            </span>
          ) : (
            <Failure conversationId={conversationId} message={message} />
          ))}
      </p>
    </article>
  );
}

/** The red line under a message that did not go out, with the way back. */
function Failure({
  conversationId,
  message,
}: {
  conversationId: string;
  message: MessageView;
}): React.JSX.Element {
  const client = useQueryClient();
  const failure = readableFailure(message.failureReason ?? '');

  const retry = useMutation({
    mutationFn: () => retryMessage(conversationId, message.id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['conversation', conversationId] });
    },
  });

  return (
    <span className="bubble__failure" title={failure.detail}>
      {failure.text}

      {failure.retryable && (
        <button
          type="button"
          className="bubble__retry"
          onClick={() => retry.mutate()}
          disabled={retry.isPending}
        >
          {retry.isPending ? t('inbox.failureRetrying') : t('inbox.failureRetry')}
        </button>
      )}
    </span>
  );
}

/**
 * Offering the customer something to tap.
 *
 * Buttons only, not lists. A list is the right control for ten opening hours and the wrong one for
 * the question an agent asks in the middle of a conversation, and a composer that offers both would
 * be a form with a mode switch in a footer that is already full. A list belongs to a bot flow,
 * where the options are written once and reused — that is the automation builder's job, not this
 * one's.
 *
 * The id is derived from the title rather than asked for. An agent typing a quick reply is not
 * thinking about stable identifiers, and asking them to would be asking them to do the automation's
 * bookkeeping; what matters is that the two ids differ, which the derivation guarantees by
 * numbering.
 */
function QuickReplyComposer({
  conversationId,
  onSent,
  onError,
}: {
  conversationId: string;
  onSent: () => void;
  onError: (message: string | null) => void;
}): React.JSX.Element {
  const [body, setBody] = useState('');
  const [titles, setTitles] = useState<string[]>(['', '']);

  const filled = titles.map((title) => title.trim()).filter(Boolean);
  const ready = body.trim() !== '' && filled.length > 0;

  const send = useMutation({
    mutationFn: () =>
      sendInteractive(conversationId, {
        kind: 'BUTTONS',
        body: body.trim(),
        buttons: filled.map((title, index) => ({ id: `opcao-${index + 1}`, title })),
      }),
    onSuccess: () => {
      setBody('');
      setTitles(['', '']);
      onError(null);
      onSent();
    },
    onError: (caught: unknown) => onError(messageFor(caught)),
  });

  const edit = (index: number, value: string): void =>
    setTitles(titles.map((title, position) => (position === index ? value : title)));

  return (
    <div className="quick-reply">
      <label className="field">
        <span>{t('inbox.quickReplyQuestion')}</span>
        <textarea
          rows={2}
          value={body}
          maxLength={INTERACTIVE_LIMITS.body}
          placeholder={t('inbox.quickReplyPlaceholder')}
          onChange={(event) => setBody(event.target.value)}
        />
      </label>

      <div className="quick-reply__options">
        {titles.map((title, index) => (
          <label key={index} className="field">
            <span>{t('inbox.quickReplyOption').replace('{n}', String(index + 1))}</span>
            <input
              value={title}
              maxLength={INTERACTIVE_LIMITS.buttonTitle}
              onChange={(event) => edit(index, event.target.value)}
            />
          </label>
        ))}
      </div>

      <div className="cell-inline">
        {titles.length < INTERACTIVE_LIMITS.buttons && (
          <button
            type="button"
            className="button button--quiet button--compact"
            onClick={() => setTitles([...titles, ''])}
          >
            + {t('inbox.quickReplyAdd')}
          </button>
        )}

        <span className="muted small">
          {t('inbox.quickReplyLimit').replace('{n}', String(INTERACTIVE_LIMITS.buttons))}
        </span>

        <button
          type="button"
          className="button button--primary button--compact"
          disabled={!ready || send.isPending}
          onClick={() => send.mutate()}
        >
          {send.isPending ? t('inbox.sending') : t('inbox.send')}
        </button>
      </div>
    </div>
  );
}

/**
 * Sending an approved template when the window has closed.
 *
 * The values are typed per component because WhatsApp's parameters are positional: `{{1}}`, `{{2}}`,
 * in order, and the count comes from the template's own text.
 */
function TemplateSender({
  conversationId,
  templates,
  onSent,
}: {
  conversationId: string;
  templates: SessionWindow['templates'];
  onSent: () => void;
}): React.JSX.Element {
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? '');
  const [values, setValues] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);

  const template = templates.find((entry) => entry.id === templateId);

  const send = useMutation({
    mutationFn: () => sendTemplate(conversationId, { templateId, values }),
    onSuccess: () => {
      setValues({});
      setError(null);
      onSent();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <span className="template-sender">
      <select
        className="select"
        value={templateId}
        onChange={(event) => {
          setTemplateId(event.target.value);
          setValues({});
        }}
        aria-label={t('inbox.chooseTemplate')}
      >
        {templates.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.name} ({entry.language})
          </option>
        ))}
      </select>

      {template?.components.map((component) => {
        const count = parameterCount(component.text ?? '');

        if (count === 0) return null;

        return (
          <span key={component.type} className="template-sender__params">
            {Array.from({ length: count }, (_, index) => (
              <input
                key={index}
                className="template-sender__input"
                placeholder={`${component.type} {{${index + 1}}}`}
                value={values[component.type]?.[index] ?? ''}
                onChange={(event) =>
                  setValues((current) => {
                    const next = [...(current[component.type] ?? [])];
                    next[index] = event.target.value;

                    return { ...current, [component.type]: next };
                  })
                }
              />
            ))}
          </span>
        );
      })}

      <button
        type="button"
        className="button button--primary button--compact"
        disabled={!templateId || send.isPending}
        onClick={() => send.mutate()}
      >
        {send.isPending ? t('inbox.sending') : t('inbox.sendTemplate')}
      </button>

      {error && <span className="template-sender__error">{error}</span>}
    </span>
  );
}

/** The highest `{{n}}` in the text decides how many values the component takes. */
function parameterCount(text: string): number {
  const found = [...text.matchAll(/\{\{\s*(\d{1,2})\s*\}\}/g)].map((match) => Number(match[1]));

  return found.length === 0 ? 0 : Math.max(...found);
}

/**
 * What this conversation is about (Phase 116).
 *
 * Above the thread rather than in the side panel, because it is read once — when you open a
 * conversation you did not have — and then never again, and the side panel is where you go to look
 * something up. It draws from what is stored and calls no model; the button is the only thing that
 * spends, which is why it is a button and not a thing that happens when the screen opens.
 *
 * A summary the thread has moved past is shown with what it says, marked as out of date. Hiding it
 * would be worse: four lines about the first half of a conversation still beats nothing, as long as
 * nobody is told they are current.
 */
